import cx from 'classnames'
import {ReactComponent as CrossIcon} from 'icons/cross.svg'
import {noop} from 'lodash'
import {ReactNode, createContext, useCallback, useContext, useState} from 'react'
import {v4 as uuid} from 'uuid'

type Notification = {
  type: 'success' | 'failure'
  message: string
}

type IdentifiableNotification = Notification & {
  id: string
}

const ToasterContext = createContext<(notification: Notification) => void>(noop)

type Props = {
  children: ReactNode
}

export const Toaster = ({children}: Props) => {
  const [notifications, setNotifications] = useState<IdentifiableNotification[]>([])

  const removeNotification = useCallback((id: string) => {
    setNotifications((notifications) => {
      return notifications.filter((notification) => notification.id !== id)
    })
  }, [])

  const addNotification = useCallback(
    (notification: Notification) => {
      const id = uuid()

      setNotifications((notifications) => {
        return [...notifications, {...notification, id}]
      })

      setTimeout(() => removeNotification(id), 10000)
    },
    [removeNotification]
  )

  return (
    <ToasterContext.Provider value={addNotification}>
      <div className="fixed top-0 w-full d:w-[968px] px-4 d:px-0 mt-4 mx-auto flex flex-col gap-4">
        {notifications.map((notification) => {
          return (
            <div
              key={notification.id}
              className={cx('rounded-lg flex p-4 justify-between', {
                'bg-[#FFD8D8]': notification.type === 'failure',
                'bg-green-300': notification.type === 'success',
              })}
            >
              <div>{notification.message}</div>
              <CrossIcon
                className="cursor-pointer hover:opacity-80"
                onClick={() => removeNotification(notification.id)}
              />
            </div>
          )
        })}
      </div>
      {children}
    </ToasterContext.Provider>
  )
}

export const useNotifier = () => {
  return useContext(ToasterContext)
}

export const useFailureNotifier = () => {
  const notifier = useNotifier()
  return useCallback(
    (error: string | {message: string}) =>
      notifier({type: 'failure', message: typeof error === 'string' ? error : error.message}),
    [notifier]
  )
}
